import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { notification, Select, DatePicker, Tabs, Button } from "antd";
import { CopyOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import service from "../../services/Service";
import { withLocale } from "../../l10n";
import Loading from "../../utils/loading";
import { iccValidation } from "../../utils/formValidation";
import withNavigation from '../hooks/withNavigation';
import M2MSubscriptionFields from '../M2MSubscriptionFields';
import { STATE_ACTIVE, STATE_CANCELLED, STATE_INACTIVE, KICONEX_UUID } from "../../utils/constants.js";
import { 
  useGetModemTypesLookupQuery, 
  useGetModelsLookupQuery,
  useGetVersionsLookupQuery, 
  useGetTenantsLookupQuery,
  invalidateRoutersLookup  
} from '../../services/lookupApi.js';

import ModemInfo from '../ModemInfo';

const EMPTY_ARRAY = [];

function RouterCard({ lang, navigate, newRouter, ident, editable, layer = 'stock' }) {
  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false });

    const [data, setData] = useState({ 
      freeModems: [], 
      modem: null 
    });

  const [form, setForm] = useState({
    uuid: "",
    modem_imei: "",
    modem_type_uuid: "",
    model_uuid: "",
    version_uuid: "",
    date: dayjs().format('DD-MM-YYYY'),
    order_id: "",
    tenant_uuid: "",
    vpn: false,
    state: "Fabricado",
  });

  const [m2m, setM2M] = useState({
    icc: "",
    state: "",
    hiring_date: null,
    renewal_date: null,
    renewal_interval: 0,
  });

  // ===== 2. SELECTORES =====
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();

  // ===== 3. DERIVADOS =====

  /**
   * Filtra el listado de los tipos demodem compatibles con routers
   */
  const routerCapableTypes = useMemo(
    () => new Set(
      modemTypes
        .filter(type => (type.models ?? []).some(model => model.device_type === 'router'))
        .map(type => type.uuid)
    ),
    [modemTypes]
  );

  /**
   * Calcula los modems libres que son compatibles con routers
   */
  const selectableModems = useMemo(
    () => data.freeModems.filter(modem => routerCapableTypes.has(modem.modem_type_uuid)),
    [data.freeModems, routerCapableTypes]
  );

  /**
   * Calcula los modelos de router compatibles con el tipo del modem elegido
   */
  const compatibleModels = useMemo(() => {
    const type = modemTypes.find(item => item.uuid === form.modem_type_uuid);

    if (!type) return EMPTY_ARRAY;

    const allowed = new Set(
      (type.models ?? []).filter(model => model.device_type === 'router').map(model => model.uuid)
    );

    return models.filter(model => allowed.has(model.uuid));
    
  }, [form.modem_type_uuid, modemTypes, models]);

  /**
   * Filtra las versiones en funcion del modelo seleccionado
   */
  const filteredVersions = useMemo(
    () => versions.filter(version => version.model_uuid === form.model_uuid),
    [versions, form.model_uuid]
  );

  /**
   * Obtiene el nombre de la organización asociada al router
   */
  const tenantName = useMemo(
    () => tenants.find(tenant => tenant.uuid === form.tenant_uuid)?.name ?? '',
    [tenants, form.tenant_uuid]
  );

  const showAssignFields = layer === 'assign' || layer === 'full';
  const isAssign = layer === 'assign';
  const isFull = layer === 'full';
  const showM2MSubscription = isAssign || (isFull && !!m2m.state);

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  /**
   * Copia un valor al portapapeles y muestra una notificación de éxito o error
   */
  const copyToClipboard = (value, fieldName) => {
    navigator.clipboard.writeText(value).then(() => {
      notification.success({
        message: `${lang.handle(fieldName)} ${lang.handle('copiado al portapapeles')}`,
      });
    }).catch(err => {
      console.error(err);
      notification.error({
        message: `${lang.handle('Error al copiar')}: ${lang.handle(fieldName)}`,
      });
    });
  };

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetchFreeModems = useCallback(async () => {
    if (!newRouter) return;

    try {
      const res = await service.get("modems", { free: true });
      if (!res || res.data.ok === false) return;
      setData(prev => ({ ...prev, freeModems: res.data.data || [] }));
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [newRouter]);

  const Pfetch = useCallback(async () => {
    if (!ident) return;

    updateUI("loading", true);
    try {
      const res = await service.get("routers/" + ident);
      if (!res || res.data.ok === false) {
        notification["error"]({ message: lang.handle(res?.data?.message) });
        return;
      }
      const router = res.data.data;
      setForm({
        uuid: router.uuid,
        modem_imei: router.modem_imei,
        modem_type_uuid: router.modem_type_uuid,
        model_uuid: router.model_uuid,
        version_uuid: router.version_uuid,
        date: router.date ? dayjs(router.date).format('DD-MM-YYYY') : dayjs().format('DD-MM-YYYY'),
        order_id: router.order_id ?? "",
        tenant_uuid: router.tenant_uuid ?? "",
        vpn: !!router.vpn,
        state: router.state,
      });

      setData(prev => ({ ...prev, modem: {
        imei: router.modem_imei,
        modem_type_code: router.modem_type_code,
        modem_serial_number: router.modem_serial_number,
        modem_mac: router.modem_mac,
        modem_device_password: router.modem_device_password,
      }}));

      setM2M({
        icc: router.icc ?? "",
        state: router.m2m_state ?? "",
        hiring_date: router.m2m_hiring_date ? dayjs(router.m2m_hiring_date).format('DD-MM-YYYY') : null,
        renewal_date: router.m2m_renewal_date ? dayjs(router.m2m_renewal_date).format('DD-MM-YYYY') : null,
        renewal_interval: router.m2m_renewal_interval ?? 0,
      });

    } catch (err) {
      console.log("Error devuelto: " + err);
    } finally {
      updateUI("loading", false);
    }
  }, [ident, lang]);

  /**
   * Valida los campos relacionados con la suscripción M2M del router
   */
  const validateM2M = () => {
    if (!m2m.state) {
      notification["error"]({ message: lang.handle('Debe seleccionar un estado de suscripción M2M') });
      return false;
    }
    if ([STATE_INACTIVE, STATE_ACTIVE].includes(m2m.state)
        && (!m2m.renewal_interval || Number(m2m.renewal_interval) === 0)) {
      notification["error"]({ message: lang.handle('Estado de suscripción no compatible con el intervalo seleccionado') });
      return false;
    }
    if ([STATE_INACTIVE, STATE_ACTIVE, STATE_CANCELLED].includes(m2m.state) && !m2m.icc) {
      notification["error"]({ message: lang.handle('ICC es obligatorio') });
      return false;
    }
    if (m2m.icc && !iccValidation(m2m.icc)) {
      notification["error"]({ message: lang.handle('¡ICC erróneo!') });
      return false;
    }
    return true;
  };

  /**
   * Valida los campos del formulario principal del router
   */
  const validate = () => {

    if (!form.modem_imei) {
      notification["error"]({ message: lang.handle('Modem es un campo obligatorio') });
      return false;
    }
    if (!form.model_uuid || !form.version_uuid) {
      notification["error"]({ message: lang.handle('Modelo y versión son campos obligatorios') });
      return false;
    }
    if (showAssignFields) {
      if (!form.order_id) {
        notification["error"]({ message: lang.handle('Pedido es un campo obligatorio') });
        return false;
      }
      if (!form.tenant_uuid) {
        notification["error"]({ message: lang.handle('Organización es un campo obligatorio') });
        return false;
      }
    }
    if (showM2MSubscription && !validateM2M()) {
      return false;
    }

    return true;
  };

  // ===== 6. HANDLERS =====
  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateForm(name, type === 'checkbox' ? checked : value);
  };

  const onModemChange = (imei) => {
    const modem = selectableModems.find(item => item.imei === imei);

    setForm(prev => ({
      ...prev,
      modem_imei: imei,
      modem_type_uuid: modem?.modem_type_uuid ?? "",
      model_uuid: "",
      version_uuid: "",
    }));
  };

  const onChangeDate = (date, dateString) => {
    updateForm('date', dateString);
  };

  const onModelChange = (value) => {
    setForm(prev => ({ ...prev, model_uuid: value, version_uuid: "" }));
  };

  const handleKiteSaved = useCallback(() => {
    if (!isAssign) return;

    if (form.tenant_uuid === KICONEX_UUID
        && m2m.state !== STATE_ACTIVE
        && m2m.state !== STATE_CANCELLED) {
      setM2M(prev => ({ ...prev, state: STATE_INACTIVE }));
    }
  }, [isAssign, form.tenant_uuid, m2m.state]);


  const buildPayload = () => {
    // El alta va a POST /routers, que espera los campos en plano
    if (newRouter) {
      return { 
        modem_imei: form.modem_imei, 
        version_uuid: form.version_uuid, 
        date: form.date 
      };
    }

    // La asignacion es una operacion aparte: crea la suscripcion y cambia el
    // estado en una transaccion. Solo viajan los campos de la fase.
    if (isAssign) {
      return {
        action: 'assign',
        router: {
          order_id: form.order_id || null,
          tenant_uuid: form.tenant_uuid || null,
          vpn: form.vpn,
        },
        m2m_subscription: m2m,
      };
    }

    // Edicion completa: router y suscripcion juntos, porque el ICC vive en ella
    if (showAssignFields) {
      return {
        router: {
          modem_imei: form.modem_imei,
          version_uuid: form.version_uuid,
          date: form.date,
          order_id: form.order_id || null,
          tenant_uuid: form.tenant_uuid || null,
          vpn: form.vpn,
          state: form.state,
        },
        m2m_subscription: m2m,
      };
    }

    // Stock: solo datos tecnicos, aqui no hay suscripcion todavia
    return {
      router: { 
        modem_imei: form.modem_imei, 
        version_uuid: form.version_uuid, 
        date: form.date 
      },
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = buildPayload();

    try {
      const res = newRouter
        ? await service.post("routers", payload)
        : await service.put("routers/" + form.uuid, payload);

      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
        return;
      }

      notification["success"]({
        message: lang.handle(
          newRouter ? "Router creado correctamente"
            : isAssign ? "Router asignado correctamente"
              : "Router actualizado correctamente"
        ),
      });

      dispatch(invalidateRoutersLookup());

      navigate(
        isAssign ? "/boards/manufacture/assigned/3"
          : isFull ? "/boards/list/3"
            : "/boards/manufacture/list/3"
      );
    } catch (err) {
      console.log("Error: " + err);
    }
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    fetchFreeModems();
  }, [fetchFreeModems]);

  useEffect(() => {
    if (ident) Pfetch();
  }, [ident, Pfetch]);

  // Preseleccion del modem cuando se llega desde el boton de Stock Teltonika
  useEffect(() => {
    if (!newRouter) return;
    const imei = searchParams.get('modem_imei');
    if (!imei) return;

    const modem = selectableModems.find(item => item.imei === imei);
    if (!modem) return;

    setForm(prev => (prev.modem_imei === imei ? prev : {
      ...prev,
      modem_imei: imei,
      modem_type_uuid: modem.modem_type_uuid,
      model_uuid: "",
      version_uuid: "",
    }));
  }, [newRouter, searchParams, selectableModems]);

  // ===== 8. CONFIG =====
  const title = newRouter
    ? lang.handle("Nuevo router")
    : (isAssign ? lang.handle("Asignar router") : lang.handle("Información de router"))
      + " " + form.modem_imei;

  const modemOptions = useMemo(
    () => selectableModems.map(modem => ({
      value: modem.imei,
      label: `${modem.imei} — ${modem.modem_type_code}`,
    })),
    [selectableModems]
  );

  const modelOptions = useMemo(
    () => compatibleModels.map(model => ({ value: model.uuid, label: model.name })),
    [compatibleModels]
  );

  const versionOptions = useMemo(
    () => filteredVersions.map(version => ({ value: version.uuid, label: version.name })),
    [filteredVersions]
  );

  const tenantOptions = useMemo(
    () => tenants.map(tenant => ({ value: tenant.uuid, label: tenant.name })),
    [tenants]
  );

  const stateOptions = useMemo(() => [
    { value: 'Fabricado', label: lang.handle('Fabricado') },
    { value: 'Asignado', label: lang.handle('Asignado') },
    { value: 'Terminado', label: lang.handle('Terminado') },
    { value: 'Eliminado', label: lang.handle('Eliminado') },
  ], [lang]);

    // ===== 9. RENDER =====
  const routerTab = {
    label: lang.handle('Router'),
    key: '1',
    children: (
      <>
        {!newRouter && (
          <div className="form-group row">
            <label htmlFor="uuid" className="col-sm-2 col-form-label newCard-form-text">
              {lang.handle('UUID')}
            </label>
            <div className="col-sm-10 d-flex gap-2">
              <input type="text" className="form-control" id="uuid" value={form.uuid} disabled />
              <Button onClick={() => copyToClipboard(form.uuid, 'UUID')} disabled={!form.uuid}>
                <CopyOutlined />
              </Button>
            </div>
          </div>
        )}
        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle("Modem Teltonika")} </div>
          <div className="col-sm-10 d-flex gap-2">
            {newRouter ? (
              <Select
                className="form-control flex-grow-1"
                placeholder={lang.handle("Seleccionar opción")}
                value={form.modem_imei || undefined}
                onChange={onModemChange}
                options={modemOptions}
                disabled={!editable || layer !== 'stock'}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              />
            ) : (
              <input type="text" className="form-control" value={form.modem_imei} disabled />
            )}
            <Button onClick={() => copyToClipboard(form.modem_imei, 'IMEI')} disabled={!form.modem_imei}>
              <CopyOutlined />
            </Button>
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle('Fecha fabricación')} </div>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={dayjs(form.date, "DD-MM-YYYY")}
              onChange={onChangeDate}
              format="DD-MM-YYYY"
              disabled={!editable || isAssign}
              allowClear={false}
              required
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle("Modelo")} </div>
          <div className="col-sm-10">
            <Select
              className="form-control"
              placeholder={lang.handle("Seleccionar opción")}
              value={form.model_uuid || undefined}
              onChange={onModelChange}
              options={modelOptions}
              disabled={!editable || isAssign || !form.modem_type_uuid}
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle("Versión")} </div>
          <div className="col-sm-10">
            <Select
              className="form-control"
              placeholder={lang.handle("Seleccionar opción")}
              value={form.version_uuid || undefined}
              onChange={(value) => updateForm('version_uuid', value)}
              options={versionOptions}
              disabled={!editable || isAssign || !form.model_uuid}
            />
          </div>
        </div>

        {showAssignFields && (
          <>
            <div className="form-group row">
              <label htmlFor="order_id" className="col-sm-2 col-form-label newCard-form-text">
                {lang.handle("Pedido")}
              </label>
              <div className="col-sm-10">
                <input type="text" className="form-control" id="order_id" name="order_id"
                  value={form.order_id} onChange={onInputChange} disabled={!editable} required />
              </div>
            </div>

            <div className="form-group row">
              <div className="col newCard-form-text"> {lang.handle("Organización")} </div>
              <div className="col-sm-10">
                <Select
                  className="form-control"
                  placeholder={lang.handle("Seleccionar opción")}
                  value={form.tenant_uuid || undefined}
                  onChange={(value) => updateForm('tenant_uuid', value)}
                  options={tenantOptions}
                  disabled={!editable}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col newCard-form-text"> VPN </div>
              <div className="col-sm-10">
                <Select
                  className="form-control"
                  value={form.vpn ? 1 : 0}
                  onChange={(value) => updateForm('vpn', !!value)}
                  disabled={!editable}
                  options={[
                    { value: 1, label: lang.handle('Sí') },
                    { value: 0, label: lang.handle('No') },
                  ]}
                />
              </div>
            </div>
          </>
        )}

        {!newRouter && (
          <div className="form-group row">
            <div className="col newCard-form-text"> {lang.handle("Estado")} </div>
            <div className="col-sm-10">
              {isFull ? (
                <Select
                  className="form-control"
                  value={form.state}
                  onChange={(value) => updateForm('state', value)}
                  options={stateOptions}
                  disabled={!editable}
                />
              ) : (
                <input type="text" className="form-control" value={form.state} disabled />
              )}
            </div>
          </div>
        )}
      </>
    )
  };

  const subscriptionsTab = {
    label: lang.handle('Subscripción'),
    key: '2',
    children: (
      <div>
        <h3>M2M</h3>
        <M2MSubscriptionFields
          value={m2m}
          onChange={(patch) => setM2M(prev => ({ ...prev, ...patch }))}
          editable={editable}
          suggestedOwner={tenantName}
          suggestedAlias={form.order_id}
          onKiteSaved={handleKiteSaved}
        />
      </div>
    )
  };

  const modemTab = {
    label: lang.handle('Modem'),
    key: '3',
    children: <ModemInfo data={data.modem} />
  };

  /**
   * Lista de tabs que se mostrarán en la interfaz
   */
  const items = [
    routerTab,
    ...(showM2MSubscription ? [subscriptionsTab] : []),
    ...(data.modem ? [modemTab] : []),
  ];


  // ===== RENDER =====
  return (
    <div className="card">
      {ui.loading && <Loading loading={ui.loading} />}
      <h4 className="p-3"> {title} </h4>
      <form onSubmit={onSubmit} className="p-3" noValidate>
        {items.length > 1
          ? <Tabs type="card" items={items} />
          : items[0].children}

        {editable && (
          <button id="button-save" className="btn btn-primary d-flex float-right" type="submit">
            <div className="btnText">
              <p> {lang.handle("Guardar")}&nbsp;&nbsp; </p>
              <i className="material-icons"> save </i>
            </div>
          </button>
        )}
      </form>
    </div>
  );
}

export default withLocale(withNavigation(RouterCard));
